/**
 * SPDX-FileCopyrightText: 2019 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

import axios from '@nextcloud/axios'
import { showError } from '@nextcloud/dialogs'
import { generateUrl } from '@nextcloud/router'
import logger from './Logger.js'
import store from './store.js'
import { copyNote } from './Util.js'

function url(url) {
	url = `apps/notes${url}`
	return generateUrl(url)
}

function handleSyncError(message, err = null) {
	if (err?.response) {
		const statusCode = err.response?.status
		switch (statusCode) {
			case 404:
				showError(message + ' ' + t('notes', 'Note not found.'))
				break
			case 423:
				showError(message + ' ' + t('notes', 'Note is locked.'))
				break
			case 507:
				showError(message + ' ' + t('notes', 'Insufficient storage.'))
				break
			default:
				showError(message + ' HTTP ' + statusCode + ' (' + err.response.data?.errorType + ')')
		}
	} else {
		showError(message + ' ' + t('notes', 'See JavaScript console and server log for details.'))
	}
}

export function setSettings(settings) {
	return axios
		.put(url('/settings'), settings)
		.then((response) => {
			const settings = response.data
			store.app.setSettings(settings)
			return settings
		})
		.catch((err) => {
			logger.error('Updating settings has failed', { error: err })
			handleSyncError(t('notes', 'Updating settings has failed.'), err)
			throw err
		})
}

export function deleteEditorMode() {
	return axios
		.post(url('/settings/migrate'))
		.catch((err) => {
			logger.error('Deleting editor mode setting has failed', { error: err })
			throw err
		})
}

export function getDashboardData() {
	return axios
		.get(url('/notes/dashboard'))
		.then((response) => {
			return response.data
		})
		.catch((err) => {
			logger.error('Fetching notes for dashboard has failed', { error: err })
			handleSyncError(t('notes', 'Fetching notes for dashboard has failed.'), err)
			throw err
		})
}

export function fetchNotes() {
	const lastETag = store.sync.etag
	const lastModified = store.sync.lastModified
	const headers = {}
	if (lastETag) {
		headers['If-None-Match'] = lastETag
	}
	return axios
		.get(
			url('/notes' + (lastModified ? '?pruneBefore=' + lastModified : '')),
			{ headers },
		)
		.then((response) => {
			store.app.setSettings(response.data.settings)
			if (response.data.categories) {
				store.notes.setCategories(response.data.categories)
			}
			if (response.data.noteIds && response.data.notesData) {
				store.notes.updateNotes({ noteIds: response.data.noteIds, notes: response.data.notesData })
			}
			if (response.data.errorMessage) {
				showError(t('notes', 'Error from Nextcloud server: {msg}', { msg: response.data.errorMessage }))
			} else {
				store.sync.setSyncETag(response.headers.etag)
				store.sync.setSyncLastModified(response.headers['last-modified'])
			}
			return response.data
		})
		.catch((err) => {
			if (err?.response?.status === 304) {
				store.sync.setSyncLastModified(err.response.headers['last-modified'])
				return null
			} else {
				logger.error('Fetching notes has failed', { error: err })
				handleSyncError(t('notes', 'Fetching notes has failed.'), err)
				throw err
			}
		})
}

export function fetchNote(noteId) {
	return axios
		.get(url('/notes/' + noteId))
		.then((response) => {
			const localNote = store.notes.getNote(parseInt(noteId))
			// only overwrite if there are no unsaved changes
			if (!localNote || !localNote.unsaved) {
				_updateLocalNote(response.data)
			}
			return response.data
		})
		.catch((err) => {
			if (err?.response?.status === 404) {
				throw err
			} else {
				logger.error('Fetching note has failed', { noteId, error: err })
				const msg = t('notes', 'Fetching note {id} has failed.', { id: noteId })
				store.notes.setNoteAttribute({ noteId, attribute: 'error', value: true })
				store.notes.setNoteAttribute({ noteId, attribute: 'errorType', value: msg })
				return store.notes.getNote(noteId)
			}
		})
}

export function refreshNote(noteId, lastETag) {
	const headers = {}
	if (lastETag) {
		headers['If-None-Match'] = lastETag
	}
	const note = store.notes.getNote(noteId)
	const oldContent = note.content
	return axios
		.get(
			url('/notes/' + noteId),
			{ headers },
		)
		.then((response) => {
			if (note.conflict) {
				store.notes.setNoteAttribute({ noteId, attribute: 'conflict', value: response.data })
				return response.headers.etag
			}
			const currentContent = store.notes.getNote(noteId).content
			store.notes.setNoteAttribute({ noteId, attribute: 'internalPath', value: response.data.internalPath })
			// only update if local content has not changed
			if (oldContent === currentContent) {
				_updateLocalNote(response.data)
				return response.headers.etag
			}
			return null
		})
		.catch((err) => {
			if (err?.response?.status === 304 || note.deleting) {
				// ignore error if note is deleting or not changed
				return null
			} else if (err?.code === 'ECONNABORTED') {
				// ignore cancelled request
				logger.debug('Refresh Note request was cancelled.')
				return null
			} else {
				logger.error('Refreshing note has failed', { noteId, error: err })
				handleSyncError(t('notes', 'Refreshing note {id} has failed.', { id: noteId }), err)
			}
			return null
		})
}

export function setTitle(noteId, title) {
	return axios
		.put(url('/notes/' + noteId + '/title'), { title })
		.then((response) => {
			store.notes.setNoteAttribute({ noteId, attribute: 'title', value: response.data.title })
			// need to update the internal path as well since sharing sidebar uses it
			store.notes.setNoteAttribute({ noteId, attribute: 'internalPath', value: response.data?.internalPath })
		})
		.catch((err) => {
			logger.error('Renaming note has failed', { noteId, error: err })
			handleSyncError(t('notes', 'Renaming note {id} has failed.', { id: noteId }), err)
			throw err
		})
}

export function createNote(category, title, content) {
	return axios
		.post(url('/notes'), {
			category: category || '',
			content: content || '',
			title: title || '',
		})
		.then((response) => {
			_updateLocalNote(response.data)
			return response.data
		})
		.catch((err) => {
			logger.error('Creating new note has failed', { error: err })
			handleSyncError(t('notes', 'Creating new note has failed.'), err)
			throw err
		})
}

function _updateLocalNote(note, reference) {
	if (reference === undefined) {
		reference = copyNote(note, {})
	}
	store.notes.updateNote(note)
	store.notes.setNoteAttribute({ noteId: note.id, attribute: 'reference', value: reference })
}

function _updateNote(note) {
	const requestOptions = { headers: { 'If-Match': '"' + note.etag + '"' } }
	return axios
		.put(url('/notes/' + note.id), { content: note.content }, requestOptions)
		.then((response) => {
			note.saveError = false
			store.notes.setNoteAttribute({ noteId: note.id, attribute: 'conflict', value: undefined })
			const updated = response.data
			if (updated.content === note.content) {
				// everything is fine
				// => update note with remote data
				_updateLocalNote({ ...updated, unsaved: false })
			} else {
				// content has changed locally in the meanwhile
				// => merge note, but exclude content
				_updateLocalNote(
					copyNote(updated, note, ['content']),
					copyNote(updated, {}),
				)
			}
		})
		.catch((err) => {
			if (err?.response?.status === 412) {
				// ETag does not match, try to merge changes
				note.saveError = false
				store.notes.setNoteAttribute({ noteId: note.id, attribute: 'conflict', value: undefined })
				const reference = note.reference
				const remote = err.response.data
				if (remote.content === note.content) {
					// content is already up-to-date
					// => update note with remote data
					_updateLocalNote({ ...remote, unsaved: false })
				} else if (remote.content === reference.content) {
					// remote content has not changed
					// => use all other attributes and sync again
					_updateLocalNote(
						copyNote(remote, note, ['content']),
						copyNote(remote, {}),
					)
					queueCommand(note.id, 'content')
				} else {
					logger.info('Note update conflict. Manual resolution required.', { noteId: note.id })
					store.notes.setNoteAttribute({ noteId: note.id, attribute: 'conflict', value: remote })
				}
			} else {
				store.notes.setNoteAttribute({ noteId: note.id, attribute: 'saveError', value: true })
				logger.error('Saving note has failed', { noteId: note.id, error: err })
				handleSyncError(t('notes', 'Saving note {id} has failed.', { id: note.id }), err)
			}
		})
}

export function conflictSolutionLocal(note) {
	note.etag = note.conflict.etag
	_updateLocalNote(
		copyNote(note.conflict, note, ['content']),
		copyNote(note.conflict, {}),
	)
	store.notes.setNoteAttribute({ noteId: note.id, attribute: 'conflict', value: undefined })
	queueCommand(note.id, 'content')
}

export function conflictSolutionRemote(note) {
	_updateLocalNote({ ...note.conflict, unsaved: false })
	store.notes.setNoteAttribute({ noteId: note.id, attribute: 'conflict', value: undefined })
}

export function autotitleNote(noteId) {
	return axios
		.put(url('/notes/' + noteId + '/autotitle'))
		.then((response) => {
			store.notes.setNoteAttribute({ noteId, attribute: 'title', value: response.data })
			refreshNote(noteId)
		})
		.catch((err) => {
			logger.error('Updating title for note has failed', { noteId, error: err })
			handleSyncError(t('notes', 'Updating title for note {id} has failed.', { id: noteId }), err)
		})
}

export function undoDeleteNote(note) {
	return axios
		.post(url('/notes/undo'), note)
		.then((response) => {
			_updateLocalNote(response.data)
			return response.data
		})
		.catch((err) => {
			logger.error('Undo delete has failed for note', { title: note.title, error: err })
			handleSyncError(t('notes', 'Undo delete has failed for note {title}.', { title: note.title }), err)
			throw err
		})
}

export async function deleteNote(noteId, onNoteDeleted) {
	store.notes.setNoteAttribute({ noteId, attribute: 'deleting', value: 'deleting' })
	try {
		await axios.delete(url('/notes/' + noteId))
	} catch (err) {
		logger.error('Deleting note has failed', { noteId, error: err })
		handleSyncError(t('notes', 'Deleting note {id} has failed.', { id: noteId }), err)
	}
	// remove note always since we don't know when exactly the error happened
	// (note could be deleted on server even if an error was thrown)
	onNoteDeleted()
	store.notes.removeNote(noteId)
}

export function setFavorite(noteId, favorite) {
	return axios
		.put(url('/notes/' + noteId + '/favorite'), { favorite })
		.then((response) => {
			store.notes.setNoteAttribute({ noteId, attribute: 'favorite', value: response.data })
		})
		.catch((err) => {
			logger.error('Toggling favorite for note has failed', { noteId, error: err })
			handleSyncError(t('notes', 'Toggling favorite for note {id} has failed.', { id: noteId }), err)
			throw err
		})
}

export function setCategory(noteId, category) {
	return axios
		.put(url('/notes/' + noteId + '/category'), { category })
		.then((response) => {
			const realCategory = response.data
			if (category !== realCategory) {
				handleSyncError(t('notes', 'Updating the note\'s category has failed. Is the target directory writable?'))
			}
			store.notes.setNoteAttribute({ noteId, attribute: 'category', value: realCategory })
			// the endpoint answers with the category alone, but the file moves with
			// it, so the new path has to come from a refetch, whose own failure
			// fetchNote() has already reported
			return fetchNote(noteId).catch(() => {})
		})
		.catch((err) => {
			logger.error('Updating the category for note has failed', { noteId, error: err })
			handleSyncError(t('notes', 'Updating the category for note {id} has failed.', { id: noteId }), err)
			throw err
		})
}

export function renameCategory(oldCategory, newCategory) {
	return axios
		.patch(url('/notes/category'), null, { params: { oldCategory, newCategory } })
		.then((response) => {
			return response.data
		})
		.catch((err) => {
			logger.error('Renaming category has failed', { oldCategory, error: err })
			handleSyncError(t('notes', 'Renaming category "{category}" has failed.', { category: oldCategory }), err)
			throw err
		})
}

export function deleteCategory(category) {
	return axios
		.delete(url('/notes/category'), { params: { category } })
		.then((response) => {
			return response.data
		})
		.catch((err) => {
			logger.error('Deleting category has failed', { category, error: err })
			handleSyncError(t('notes', 'Deleting category "{category}" has failed.', { category }), err)
			throw err
		})
}

export function queueCommand(noteId, type) {
	store.sync.addToQueue({ noteId, type })
	_processQueue()
}

function _processQueue() {
	const queue = Object.values(store.sync.queue)
	if (store.app.isSaving || queue.length === 0) {
		return
	}
	store.app.setSaving(true)
	store.sync.clearQueue()

	async function _executeQueueCommands() {
		for (const cmd of queue) {
			try {
				switch (cmd.type) {
					case 'content':
						await _updateNote(store.notes.notesIds[cmd.noteId])
						break
					case 'autotitle':
						await autotitleNote(cmd.noteId)
						break
					default:
						logger.error('Unknown queue command', { type: cmd.type })
				}
			} catch (e) {
				logger.error('Command has failed with error', { error: e })
			}
		}
		store.app.setSaving(false)
		store.app.setManualSave(false)
		_processQueue()
	}
	_executeQueueCommands()
}

export function saveNoteManually(noteId) {
	store.notes.setNoteAttribute({ noteId, attribute: 'saveError', value: false })
	store.app.setManualSave(true)
	queueCommand(noteId, 'content')
}

export function noteExists(noteId) {
	return store.notes.noteExists(noteId)
}

export function getCategories(maxLevel, details) {
	const categories = store.notes.getCategories(maxLevel, details)
	if (maxLevel === 0) {
		return [...new Set([...categories, ...store.notes.categories])]
	} else {
		return categories
	}
}
